// services/notaireProvider.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { NotaireContext } from "../context/NotaireContext";
import { useMemberstack } from "@memberstack/react";
import { fetchAirtableRecord } from "./airtable";
import Loading from "../components/Loading";

export const NotaireProvider = ({ children }) => {
  const memberstack = useMemberstack();
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);

  const [data, setData] = useState({
    member: null,
    user: null,
    infoNotaire: null,
    infoEtude: null,
    demandeContact: [],
    demandeRdv: [],
    immo: [],
    pma: [],
    mariage: [],
    pacs: [],
    certification: [],
    bail: [],
    donation: [],
    divorce: [],
    succession: [],
    adoption: [],
    testament: [],
    procuration: [],
    "statut-societe": [],
    calendriers: null,
  });

  // Répartit les enregistrements de "autre_demarche" dans les bons tableaux
  const splitAutreDemarche = (records = []) => {
    const out = {
      testament: [],
      procuration: [],
      adoption: [],
      "statut-societe": [],
    };

    const normalize = (v) =>
      String(v ?? "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "-")
        .replace(/\s+/g, "-");

    const mapTypeToKey = (typeValue) => {
      const t = normalize(typeValue);
      if (t === "testament" || t === "testaments") return "testament";
      if (t === "procuration" || t === "procurations") return "procuration";
      if (t === "adoption" || t === "adoptions") return "adoption";

      if (
        t === "statut-societe" ||
        t === "statuts-societe" ||
        t === "statut-de-societe" ||
        t === "statut-société" ||
        t === "statuts-société" ||
        t === "statut-de-société" ||
        t === "statut-societe-" ||
        t === "statut-societe--"
      ) {
        return "statut-societe";
      }

      return null;
    };

    for (const r of records) {
      const key = mapTypeToKey(r?.fields?.type);
      if (key && out[key]) out[key].push(r);
    }

    return out;
  };

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);

      const { data: member } = await memberstack.getCurrentMember();
      const airtableId = member?.metaData?.airtable_id;

      if (!member) {
        console.warn("Pas de membre détecté dans NotaireProvider");
        return;
      }

      if (!airtableId) {
        console.warn("Membre sans airtable_id (onboarding non terminé ?)");
        // ici tu peux rediriger côté UI si besoin, mais on évite de fetch Airtable "undefined"
        setData((prev) => ({ ...prev, member }));
        return;
      }

      // ⚠️ Ton token Airtable est exposé côté front : à sécuriser plus tard via backend
      const user = await fetch(
        `https://api.airtable.com/v0/appD48APNaGA4GN0B/user/${airtableId}`,
        {
          headers: {
            Authorization:
              "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02",
            "content-type": "application/x-www-form-urlencoded",
            Accept: "application/json, text/plain, */*",
          },
        }
      )
        .then((res) => res.json())
        .then((res) => res?.fields ?? null);

      if (!user?.email) {
        console.warn("User Airtable sans email, impossible de charger les données liées.");
        setData((prev) => ({ ...prev, member, user }));
        return;
      }

      // Pour une secrétaire, on utilise l'email/id du notaire pour charger ses données
      const isSecretaire = user.role === "secretaire";
      const emailForQueries = isSecretaire && user.email_notaire ? user.email_notaire : user.email;
      const airtableIdForQueries = isSecretaire && user.notaire_airtable_id ? user.notaire_airtable_id : airtableId;

      const [
        infoNotaire,
        infoEtude,
        demandeContact,
        demandeRdv,
        immo,
        pma,
        mariage,
        pacs,
        certification,
        bail,
        donation,
        succession,
        divorce,
        autreDemarche,
      ] = await Promise.all([
        fetchAirtableRecord("info-notaire", `SEARCH("${emailForQueries}", {user})`),
        fetchAirtableRecord("etudes", `SEARCH("${emailForQueries}", {user})`),
        fetchAirtableRecord("contact-notaire", `SEARCH("${emailForQueries}", {notaire})`),

        fetchAirtableRecord("demande_rdv", `SEARCH("${airtableIdForQueries}", {id_notaire})`),

        fetchAirtableRecord("transaction", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("pma", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("mariage", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("pacs", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("certification", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("bail", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("donation", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("succession", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("divorce", `SEARCH("${emailForQueries}", {notaire})`),
        fetchAirtableRecord("autre_demarche", `SEARCH("${emailForQueries}", {notaire})`),
      ]);

      const grouped = splitAutreDemarche(autreDemarche ?? []);

      setData({
        member,
        user,
        notaireEmail: emailForQueries,
        notaireAirtableId: airtableIdForQueries,
        infoNotaire: infoNotaire?.[0]?.fields ?? null,
        infoEtude: infoEtude?.[0]?.fields ?? null,
        demandeContact: demandeContact ?? [],
        demandeRdv: demandeRdv ?? [],
        immo: immo ?? [],
        pma: pma ?? [],
        mariage: mariage ?? [],
        pacs: pacs ?? [],
        certification: certification ?? [],
        divorce: divorce ?? [],
        bail: bail ?? [],
        donation: donation ?? [],
        succession: succession ?? [],
        adoption: grouped.adoption ?? [],
        testament: grouped.testament ?? [],
        procuration: grouped.procuration ?? [],
        "statut-societe": grouped["statut-societe"] ?? [],
        calendriers: null,
      });
    } catch (e) {
      console.error("Erreur de chargement du contexte notaire", e);
    } finally {
      setLoading(false);
    }
  }, [memberstack]);

  useEffect(() => {
    const init = async () => {
      const { data: currentMember } = await memberstack.getCurrentMember();

      if (!currentMember) {
        console.warn("Pas de membre détecté dans NotaireProvider");
        setInitializing(false);
        return;
      }

      await loadAll();
      setInitializing(false);
    };

    init();
  }, [memberstack, loadAll]);

  const contextValue = useMemo(
    () => ({
      ...data,
      reloadAffaires: loadAll,
    }),
    [data, loadAll]
  );

  if (initializing || loading) return <Loading />;

  return (
    <NotaireContext.Provider value={contextValue}>
      {children}
    </NotaireContext.Provider>
  );
};